import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/AppShell";
import { Store, Star } from "lucide-react";
import { listVerifiedStores, type VerifiedStoreRow } from "@/lib/store-verification.functions";
import { VerifiedBusinessBadge, VerifiedStoreBadge } from "@/components/VerifiedBadges";

export const Route = createFileRoute("/verified-stores")({
  head: () => ({
    meta: [
      { title: "Verified Stores — Want To Buy" },
      { name: "description", content: "Browse identity-verified trading card stores on Want To Buy." },
    ],
  }),
  component: VerifiedStoresPage,
});

function VerifiedStoresPage() {
  const fn = useServerFn(listVerifiedStores);
  const [stores, setStores] = useState<VerifiedStoreRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tcgFilter, setTcgFilter] = useState("");
  const [locFilter, setLocFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fn({ data: { tcg: tcgFilter || undefined, location: locFilter || undefined } });
        setStores(r.stores);
      } finally {
        setLoading(false);
      }
    })();
  }, [fn, tcgFilter, locFilter]);

  const tcgChoices = useMemo(() => {
    const s = new Set<string>();
    stores.forEach((r) => (r.tcg_specialisations ?? []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [stores]);

  return (
    <AppShell>
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-primary">Trust & connection platform</div>
        <h1 className="mt-1 font-display text-3xl font-bold flex items-center gap-2">
          <Store className="h-7 w-7 text-indigo-500" /> Verified Stores directory
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Identity-verified physical stores selling on Want To Buy. Hand-reviewed by our team — these accounts are
          the highest trust tier on the platform.
        </p>
      </header>

      <div className="mb-6 panel flex flex-wrap items-end gap-3 p-4">
        <label className="text-xs">
          <span className="mb-1 block uppercase tracking-wider text-muted-foreground">TCG specialisation</span>
          <select
            value={tcgFilter}
            onChange={(e) => setTcgFilter(e.target.value)}
            className="h-10 rounded-md border border-border bg-background px-3 text-sm"
          >
            <option value="">All TCGs</option>
            {tcgChoices.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Location</span>
          <input
            value={locFilter}
            onChange={(e) => setLocFilter(e.target.value)}
            placeholder="City"
            className="h-10 rounded-md border border-border bg-background px-3 text-sm"
          />
        </label>
        {(tcgFilter || locFilter) && (
          <button
            onClick={() => { setTcgFilter(""); setLocFilter(""); }}
            className="btn-ghost border border-border px-3 py-2 text-xs"
          >
            Reset filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : stores.length === 0 ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">No verified stores match yet.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {stores.map((s) => (
            <Link
              key={s.id}
              to="/profile/$username"
              params={{ username: s.username }}
              className="panel p-5 transition hover:border-primary/40"
            >
              <div className="font-display text-lg font-bold">{s.store_name ?? `@${s.username}`}</div>
              <div className="text-xs text-muted-foreground">@{s.username}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                <VerifiedStoreBadge city={s.store_city} province={s.store_province} />
                {s.verified_business && <VerifiedBusinessBadge />}
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs">
                {s.seller_rating_avg != null && (s.seller_rating_count ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 text-warning">
                    <Star className="h-3 w-3 fill-warning text-warning" />
                    {Number(s.seller_rating_avg).toFixed(1)} ({s.seller_rating_count})
                  </span>
                )}
                <span className="text-muted-foreground">{s.seller_completed_count ?? 0} trades</span>
              </div>
              {(s.tcg_specialisations ?? []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1 text-[10px]">
                  {(s.tcg_specialisations ?? []).slice(0, 5).map((t) => (
                    <span key={t} className="rounded-full border border-border bg-background/60 px-2 py-0.5 text-muted-foreground">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  );
}
